import { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { MapPin } from "lucide-react";
import type { GeographyLocation } from "@/api/geography";
import { logger, serializeError } from "@/lib/logger";

interface GeographyMapProps {
  locations: GeographyLocation[];
}

interface MarkerTier {
  fill: string;
  border: string;
  label: string;
}

const MARKER_TIERS: { min: number; tier: MarkerTier }[] = [
  {
    min: 20,
    tier: {
      fill: "rgba(249, 115, 22, 0.85)",
      border: "rgba(194, 65, 12, 1)",
      label: "20+",
    },
  },
  {
    min: 10,
    tier: {
      fill: "rgba(139, 92, 246, 0.85)",
      border: "rgba(109, 40, 217, 1)",
      label: "10–19",
    },
  },
  {
    min: 0,
    tier: {
      fill: "rgba(59, 130, 246, 0.85)",
      border: "rgba(29, 78, 216, 1)",
      label: "1–9",
    },
  },
];

function getTier(count: number): MarkerTier {
  return (MARKER_TIERS.find((t) => count >= t.min) ?? MARKER_TIERS.at(-1)!)
    .tier;
}

// Build a MapLibre step-style `case` picking a tier colour by a numeric data
// property, so clusters and points share one tier definition (MARKER_TIERS).
// `min: 0` tier is the fallback, so only the thresholds above it need a branch.
// Branches MUST be emitted highest-threshold-first: MapLibre `case` returns the
// first truthy branch, so `>=10` before `>=20` would swallow every 20+ value
// (that's the violet-68 bug). Sort descending, don't rely on array order.
function tierColorExpression(
  property: string,
  pick: (t: MarkerTier) => string,
): maplibregl.ExpressionSpecification {
  const sorted = [...MARKER_TIERS].sort((a, b) => b.min - a.min);
  const fallback = sorted[sorted.length - 1];
  const thresholds = sorted.filter((t) => t.min > 0);
  return [
    "case",
    ...thresholds.flatMap(({ min, tier }) => [
      [">=", ["get", property], min],
      pick(tier),
    ]),
    pick(fallback.tier),
  ] as unknown as maplibregl.ExpressionSpecification;
}

export const GeographyMap = ({ locations }: GeographyMapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const [mapFailed, setMapFailed] = useState(false);

  useEffect(() => {
    if (!mapContainer.current) return;

    // Watchdog: if the map never finishes loading (silent WebGL/context failure
    // that doesn't throw, tiles that never paint), fall back to the list so the
    // page never shows a blank white box.
    let settled = false;
    const fail = (why: unknown) => {
      if (settled) return;
      settled = true;
      logger.warn("GeographyMap: falling back to list view", serializeError(why));
      setMapFailed(true);
    };
    const watchdog = window.setTimeout(() => {
      if (!map.current?.loaded()) fail("load timeout");
    }, 6000);

    // Since #339 route code-splitting, Geography mounts lazily via Suspense, so
    // the map can initialise before the container has its final size (0×0 →
    // canvas never paints = blank map). Re-measure whenever the box resizes.
    let ro: ResizeObserver | undefined;

    // MapLibre throws synchronously when WebGL is unavailable (older devices,
    // headless browsers, GPU blocklists). Catch and fall back to a list view
    // so the page never crashes with a blank screen.
    try {
      // Initialize map with OpenStreetMap raster tiles (no API key required)
      map.current = new maplibregl.Map({
        container: mapContainer.current,
        cooperativeGestures: true,
        style: {
          version: 8,
          // Glyph endpoint is required or symbol layers (cluster + point counts)
          // render no text — the OSM raster style ships none.
          glyphs:
            "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf",
          sources: {
            osm: {
              type: "raster",
              tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
              tileSize: 256,
              attribution:
                '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            },
          },
          layers: [
            {
              id: "osm",
              type: "raster",
              source: "osm",
            },
          ],
        },
        zoom: 1.5,
        center: [15, 30],
      });

      // Add navigation controls
      ro = new ResizeObserver(() => map.current?.resize());
      ro.observe(mapContainer.current);

      map.current.addControl(
        new maplibregl.NavigationControl({
          visualizePitch: true,
        }),
        "top-right",
      );

      // Async failures (WebGL context creation that doesn't throw, tile/render
      // errors) surface here — the sync try/catch alone misses them, which is
      // why the map could render a blank box instead of the fallback list.
      map.current.on("error", (e) => fail(e?.error ?? e));
      map.current.on("webglcontextlost", () => fail("webglcontextlost"));

      // Add the clustered source + layers when the map loads.
      map.current.on("load", () => {
        settled = true;
        window.clearTimeout(watchdog);
        // Fix any init-time sizing race (container measured before layout).
        map.current?.resize();
        if (!map.current) return;

        const features = locations
          .filter((loc) => loc.coordinates)
          .map((loc) => ({
            type: "Feature" as const,
            properties: {
              city: loc.city,
              country: loc.country,
              n: loc.uniqueParticipants,
            },
            geometry: {
              type: "Point" as const,
              coordinates: loc.coordinates as [number, number],
            },
          }));

        // Cluster nearby cities so Europe stops overlapping into a blob. Bubbles
        // size by SUMMED participants (clusterProperties.sum), not marker count —
        // otherwise 13 one-runner cities out-shout London.
        map.current.addSource("locations", {
          type: "geojson",
          cluster: true,
          clusterRadius: 45,
          clusterMaxZoom: 6,
          clusterProperties: { sum: ["+", ["get", "n"]] },
          data: { type: "FeatureCollection", features },
        });

        // Cluster bubbles: tier colour by SUMMED participants so a bubble's
        // colour matches the legend (a cluster summing to 2 must read blue, not
        // orange). Radius by sqrt of summed participants.
        map.current.addLayer({
          id: "clusters",
          type: "circle",
          source: "locations",
          filter: ["has", "point_count"],
          paint: {
            "circle-color": tierColorExpression("sum", (t) => t.fill),
            "circle-stroke-color": tierColorExpression("sum", (t) => t.border),
            "circle-stroke-width": 2,
            "circle-radius": ["+", 10, ["*", 2.2, ["sqrt", ["get", "sum"]]]],
          },
        });

        map.current.addLayer({
          id: "cluster-count",
          type: "symbol",
          source: "locations",
          filter: ["has", "point_count"],
          layout: {
            "text-field": ["get", "sum"],
            "text-font": ["Noto Sans Bold"],
            "text-size": 12,
          },
          paint: { "text-color": "#ffffff" },
        });

        // Unclustered points: sqrt sizing + the 3 existing colour tiers so area,
        // not diameter, reads proportionally (linear 12 + n*1.2 saturated at 36px,
        // so London 76 and Tbilisi 24 looked identical).
        map.current.addLayer({
          id: "points",
          type: "circle",
          source: "locations",
          filter: ["!", ["has", "point_count"]],
          paint: {
            "circle-radius": ["+", 6, ["*", 1.75, ["sqrt", ["get", "n"]]]],
            "circle-color": [
              "case",
              [">=", ["get", "n"], 20],
              "rgba(249, 115, 22, 0.85)",
              [">=", ["get", "n"], 10],
              "rgba(139, 92, 246, 0.85)",
              "rgba(59, 130, 246, 0.85)",
            ],
            "circle-stroke-width": 2,
            "circle-stroke-color": [
              "case",
              [">=", ["get", "n"], 20],
              "rgba(194, 65, 12, 1)",
              [">=", ["get", "n"], 10],
              "rgba(109, 40, 217, 1)",
              "rgba(29, 78, 216, 1)",
            ],
          },
        });

        // Show participant count inside points big enough to fit it (n >= 10,
        // i.e. the violet + orange tiers). Smaller blue dots stay unlabelled.
        map.current.addLayer({
          id: "point-count",
          type: "symbol",
          source: "locations",
          filter: ["all", ["!", ["has", "point_count"]], [">=", ["get", "n"], 10]],
          layout: {
            "text-field": ["get", "n"],
            "text-size": 11,
            "text-font": ["Noto Sans Bold"],
            "text-allow-overlap": true,
          },
          paint: { "text-color": "#ffffff" },
        });

        // Click a cluster → zoom to the level where it splits apart.
        map.current.on("click", "clusters", (e) => {
          if (!map.current) return;
          const feature = map.current.queryRenderedFeatures(e.point, {
            layers: ["clusters"],
          })[0];
          const clusterId = feature?.properties?.cluster_id;
          if (clusterId == null) return;
          const source = map.current.getSource(
            "locations",
          ) as maplibregl.GeoJSONSource;
          source.getClusterExpansionZoom(clusterId).then((zoom) => {
            map.current?.easeTo({
              center: (feature.geometry as GeoJSON.Point).coordinates as [
                number,
                number,
              ],
              zoom,
            });
          });
        });

        map.current.on("click", "points", (e) => {
          if (!map.current) return;
          const feature = e.features?.[0];
          if (!feature) return;
          const props = feature.properties as {
            city: string;
            country: string;
            n: number;
          };

          // Build the popup via DOM + textContent instead of setHTML so
          // DB-sourced city/country strings can never inject markup (XSS).
          const popupNode = document.createElement("div");
          popupNode.style.padding = "8px";

          const title = document.createElement("strong");
          title.textContent = `${props.city}, ${props.country}`;
          popupNode.appendChild(title);
          popupNode.appendChild(document.createElement("br"));

          const count = Number(props.n);
          const countSpan = document.createElement("span");
          countSpan.style.color = getTier(count).border;
          countSpan.style.fontWeight = "bold";
          countSpan.textContent = String(count);
          popupNode.appendChild(countSpan);
          popupNode.appendChild(
            document.createTextNode(
              ` unique participant${count === 1 ? "" : "s"}`,
            ),
          );

          new maplibregl.Popup({ offset: 15 })
            .setLngLat((feature.geometry as GeoJSON.Point).coordinates as [
              number,
              number,
            ])
            .setDOMContent(popupNode)
            .addTo(map.current);
        });

        const setPointer = (cursor: string) => {
          if (map.current) map.current.getCanvas().style.cursor = cursor;
        };
        map.current.on("mouseenter", "clusters", () => setPointer("pointer"));
        map.current.on("mouseleave", "clusters", () => setPointer(""));
        map.current.on("mouseenter", "points", () => setPointer("pointer"));
        map.current.on("mouseleave", "points", () => setPointer(""));
      });

      return () => {
        window.clearTimeout(watchdog);
        ro?.disconnect();
        map.current?.remove();
      };
    } catch (err) {
      window.clearTimeout(watchdog);
      ro?.disconnect();
      fail(err);
    }
  }, [locations]);

  if (mapFailed) {
    const sorted = [...locations].sort(
      (a, b) => b.uniqueParticipants - a.uniqueParticipants,
    );
    return (
      <div className="w-full rounded-lg border bg-card p-4 md:p-6">
        <p className="text-xs text-muted-foreground mb-4">
          Interactive map unavailable on this device. Browse our cities below:
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {sorted.map((loc) => (
            <div
              key={`${loc.city}-${loc.country}-${loc.coordinates?.join(",") ?? "nocoord"}`}
              className="flex items-start gap-2 p-3 rounded-md bg-muted/40 border"
            >
              <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{loc.city}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {loc.country} · {loc.uniqueParticipants} unique participant
                  {loc.uniqueParticipants === 1 ? "" : "s"}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[400px] md:h-[600px] rounded-lg overflow-hidden border bg-card">
      <div ref={mapContainer} className="absolute inset-0" />
      {/* Legend */}
      <div className="absolute bottom-3 left-3 bg-background/90 backdrop-blur-sm border rounded-lg p-2 text-xs space-y-1 pointer-events-none">
        {MARKER_TIERS.map((t) => (
          <div key={t.tier.label} className="flex items-center gap-2">
            <span
              className="inline-block w-3 h-3 rounded-full shrink-0"
              style={{ backgroundColor: t.tier.border }}
            />
            <span className="text-muted-foreground">
              {t.tier.label} unique participants
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
